import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useFaqStore } from 'src/store/personalization/faq/faq.store';
import * as Yup from 'yup';

export default function useFaqDetails() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  const AREAS_OPTIONS = [
    { value: 'help_page', label: 'Página de Ajuda' },
    { value: 'about_levels', label: 'Sobre os Níveis' },
    { value: 'about_points', label: 'Sobre os Pontos' },
    { value: 'about_invoice', label: 'Sobre a Nota Fiscal' },
    { value: 'invoices_page', label: 'Minhas Notas' },
    { value: 'how_to_use_points', label: 'Como usar os pontos' },
    { value: 'how_to_accumulate_points', label: 'Como acumular pontos' },
  ];

  const { getFaqDetails, createFaq, updateFaq, resetFaqDetails, isLoadingFaqDetails, faqDetails } =
    useFaqStore();

  useEffect(() => {
    if (id) {
      getFaqDetails(id);
    } else {
      resetFaqDetails();
    }
  }, [id, isEdit]);

  const defaultValues = useMemo(
    () => ({
      question: faqDetails?.question || '',
      answers: faqDetails?.answers || [],
      areas: faqDetails?.areas || [],
      status: faqDetails?.status || 'active',
    }),
    [isEdit, faqDetails]
  );

  const faqSchema = Yup.object().shape({
    question: Yup.string().required('Campo obrigatório!'),
    answers: Yup.array()
      .of(
        Yup.string()
          .required('Campo obrigatório!')
          .min(3, 'Cada resposta deve ter pelo menos 3 caracteres')
      )
      .required('Adicione pelo menos uma regra'),
    areas: Yup.array().test(
      'is-not-empty',
      'Selecione pelo menos uma opção',
      (v) => v && v.length > 0
    ),
  });

  const methods = useForm<any>({
    resolver: yupResolver(faqSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [faqDetails, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      if (!data.answers.length) {
        enqueueSnackbar('Adicione respostas para a sua pergunta!', { variant: 'error' });
        return;
      }

      if (isEdit && id) {
        await updateFaq({ ...data, _id: faqDetails?._id || id! });
        enqueueSnackbar('Faq atualizado com sucesso!');
      } else {
        await createFaq(data);
        enqueueSnackbar('Faq criado com sucesso!');
      }
      navigate(paths.dashboard.faq);
    } catch (error) {
      console.error(error);
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: control as any,
    name: 'answers' as never,
  });

  return {
    id,
    isLoadingFaqDetails,
    isEdit,
    methods,
    fields,
    AREAS_OPTIONS,
    isSubmitting,
    STATUS_OPTIONS,
    STATUS_LABEL,
    append,
    remove,
    onSubmit,
  };
}
